import type { DescribeWorkbookResponse, ExtractResponse, MergeResponse, PrefillResponse } from './types';
import { fetchAuthSession } from 'aws-amplify/auth';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

function resolveUrl(path: string): string {
  if (!API_BASE_URL) return path;
  return `${API_BASE_URL.replace(/\/$/, '')}${path}`;

}

async function postJson<T>(url: string, formData: FormData, token?: string): Promise<T> {
  const headers: Record<string, string> = {};

  // Add Authorization header if user is signed in
  try {
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    } else {
      const session = await fetchAuthSession();
      const sessionToken = session.tokens?.idToken?.toString();
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }
    }
  } catch (error) {
    // No auth session, continue as anonymous
  }

  const response = await fetch(resolveUrl(url), {
    method: 'POST',
    headers,
    body: formData
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Request failed with status ${response.status}`);
  }
  return response.json() as Promise<T>;
}

export function decodeFilePayload(payload: { data: string; contentType: string; fileName: string }): Blob {
  const binary = atob(payload.data);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: payload.contentType });
}

export async function describeWorkbook(file: File): Promise<DescribeWorkbookResponse> {
  const form = new FormData();
  form.append('workbook', file);
  return postJson<DescribeWorkbookResponse>('/describe', form);
}

export async function extractXliff(params: {
  workbook: File;
  configJson: string;
  srxFile?: File | null;
  token?: string;
}): Promise<ExtractResponse> {
  const form = new FormData();
  form.append('workbook', params.workbook);
  form.append('config', new Blob([params.configJson], { type: 'application/json' }), 'config.json');
  if (params.srxFile) {
    form.append('srx', params.srxFile, params.srxFile.name);
  }
  return postJson<ExtractResponse>('/extract', form, params.token);
}

export async function mergeXliff(params: {
  workbook: File;
  xliff: File[];
  configJson: string;
  srxFile?: File | null;
  token?: string;
}): Promise<MergeResponse> {
  const form = new FormData();
  form.append('workbook', params.workbook);
  params.xliff.forEach((file) => {
    form.append('xliff', file);
  });
  form.append('config', new Blob([params.configJson], { type: 'application/json' }), 'config.json');
  if (params.srxFile) {
    form.append('srx', params.srxFile, params.srxFile.name);
  }
  return postJson<MergeResponse>('/merge', form, params.token);
}

export async function prefillMT(params: {
  workbook: File;
  configJson: string;
  provider: 'google' | 'deepl';
  targetLanguages: string[];
  token?: string;
}): Promise<PrefillResponse> {
  const form = new FormData();
  form.append('workbook', params.workbook);
  form.append('config', new Blob([params.configJson], { type: 'application/json' }), 'config.json');
  form.append('provider', params.provider);
  form.append('targetLanguages', JSON.stringify(params.targetLanguages));
  return postJson<PrefillResponse>('/prefill', form, params.token);
}
